import { FormState } from 'react-hook-form';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import {
    EnterpriseProfileUpdateOptionParameters,
    EnterpriseProfileUpdatePrivilegeParameters,
} from '@mews-commander/data-access';
import {
    EnterpriseProfilePrivilegesEnumNames,
    EnterpriseProfilePrivileges,
} from '@mews-commander/legacy-data-structures';
import { RolesAndPermissionsOption } from '../../types';
import { FieldNames } from '../FieldNames';
import { EditUserFormValues } from './schema';

export const getSubmittedUserEditValues = () => {
    const getOptionsValue = (dirtyFields: FormState<EditUserFormValues>['dirtyFields'], values: EditUserFormValues) => {
        const finalValue: EnterpriseProfileUpdateOptionParameters = {};

        // Roles & Permissions group was unlocked, overwrite this value in properties
        if (dirtyFields.rolesAndPermissions) {
            finalValue.receiveCustomerMessages = { value: values.receiveCustomerMessages };
        }
        // Value changed
        if (dirtyFields.receiveCustomerMessages) {
            finalValue.receiveCustomerMessages = { value: values.receiveCustomerMessages };
        }
        // NoPermissions selected, enforce false
        if (dirtyFields.rolesAndPermissions && values.rolesAndPermissions === RolesAndPermissionsOption.NoPermissions) {
            finalValue.receiveCustomerMessages = { value: false };
        }
        // Allow to select reset2FA, but only for admins
        if (dirtyFields.reset2FA && values.rolesAndPermissions === RolesAndPermissionsOption.Admin) {
            finalValue.resetUsersTwoFactorAuthentication = { value: values.reset2FA };
        }
        // Something else than admin selected, enforce Reset2FA to false
        if (dirtyFields.rolesAndPermissions && values.rolesAndPermissions !== RolesAndPermissionsOption.Admin) {
            finalValue.resetUsersTwoFactorAuthentication = { value: false };
        }

        return finalValue;
    };

    const getAdminValue = (dirtyFields: FormState<EditUserFormValues>['dirtyFields'], values: EditUserFormValues) => {
        // Roles & Permissions value changed
        if (dirtyFields.rolesAndPermissions) {
            return { value: values.rolesAndPermissions === RolesAndPermissionsOption.Admin };
        }
    };

    const getRoleIdsValue = (dirtyFields: FormState<EditUserFormValues>['dirtyFields'], values: EditUserFormValues) => {
        const roleIdsSelected = values[FieldNames.RoleIds];
        const roleIds = roleIdsSelected?.length ? roleIdsSelected : null;

        // roleIds select changed
        if (dirtyFields.roleIds && values.rolesAndPermissions === RolesAndPermissionsOption.Roles) {
            return { value: roleIds };
        }

        // NoPermissions selected, un-assign all roles
        if (dirtyFields.rolesAndPermissions && values.rolesAndPermissions === RolesAndPermissionsOption.NoPermissions) {
            return { value: null };
        }
    };

    const getPrivilegesValue = (
        dirtyFields: FormState<EditUserFormValues>['dirtyFields'],
        values: EditUserFormValues
    ): EnterpriseProfileUpdatePrivilegeParameters | undefined => {
        const privilegeBooleanValue = (privilege: EnterpriseProfilePrivilegesEnumNames) => {
            // If user selects Admin or NoPermissions, force-set all privileges to false
            if (values.rolesAndPermissions !== RolesAndPermissionsOption.CustomizedPermissions) {
                return false;
            }

            return (values[FieldNames.Privileges] || []).includes(privilege);
        };

        const privileges = pipe(
            EnterpriseProfilePrivileges.enumValues,
            array.map(({ value: privilege }) => [privilege, { Value: privilegeBooleanValue(privilege) }]),
            Object.fromEntries
        );

        // Value changed or rolesAndPermissions selection changed. Skip when roles selected (or BE throws error).
        if (
            dirtyFields.privileges ||
            (dirtyFields.rolesAndPermissions && values.rolesAndPermissions !== RolesAndPermissionsOption.Roles)
        ) {
            return privileges;
        }
    };

    const getLearningPathValue = (
        dirtyFields: FormState<EditUserFormValues>['dirtyFields'],
        values: EditUserFormValues
    ) => {
        // Value changed
        if (dirtyFields.assignedLearningPath) {
            return { value: values.assignedLearningPath };
        }
    };

    return {
        getOptionsValue,
        getAdminValue,
        getRoleIdsValue,
        getPrivilegesValue,
        getLearningPathValue,
    };
};
